import React, { useEffect, useState } from "react";
import TotalCost from "./TotalCost";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";
import { TiShoppingCart } from "react-icons/ti";
import TimeSlotOptions from "./TimeSlotOptions";
import DateRangeModal from "./Modals/DateRangeModal";
import CrossSellingProducts from "./CrossSellingProducts";
import { CrossSellingProduct } from "../Types/CrossSellingProductsTypes";
import { useMutation, useQuery } from "@apollo/client";
import { GET_SKILL_LEVELS, GET_SPORT_TYPES } from "../Queries";
import {
  editMember,
  resetSelections,
  resetTimeSelection,
  setCurrentIndex,
  setTotalCost,
} from "../Redux/TeamSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../Redux/store";
import { useTranslation } from "react-i18next";
import Spinner from "../shared/Spinner";
import {
  dateToBackendFormat,
  getCartItems,
  goToShoppingCartPage,
  isMemberCompleted,
} from "../shared/helpers";
import { MEMBER_KEYS } from "../shared/enums";
import { ADD_TO_CART } from "../Mutations";
import DropDown from "../shared/DropDown";
import {
  Product,
  SkillLevel,
  SportType,
  StartTime,
} from "../Types/MemberTypes";
import ProductSlotOptions from "./ProductSlotOptions";
import { AiOutlineCalendar } from "react-icons/ai";
import { FaCheck } from "react-icons/fa";

const MembersFormComponent = () => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const checkInDate = useSelector(
    (state: RootState) => state.team.checkin_date
  );
  const members = useSelector((state: RootState) => state.team.members);
  const currentIndex = useSelector(
    (state: RootState) => state.team.current_index
  );
  const member = useSelector(
    (state: RootState) => members[state.team.current_index]
  );
  const products = member.products ?? [];
  const numOfMembers = members.length;
  const days = member.course_duration;
  const showShoppingCartBtn = currentIndex === numOfMembers - 1;
  const selectedProduct = useSelector(
    (state: RootState) => members[state.team.current_index]?.product
  );

  const [sportTypes, setSportTypes] = useState<SportType[]>([]);
  const [skillLevels, setSkillLevels] = useState<SkillLevel[]>([]);
  const [isDateRangeModalOpen, setIsDateRangeModalOpen] = useState(false);
  const [isPending, setIsPending] = useState<Boolean>(false);

  const setSelectedProduct = (selectedProduct: Product | null) => {
    dispatch(
      editMember({
        index: currentIndex,
        key: MEMBER_KEYS.Product,
        value: selectedProduct ?? undefined,
      })
    );

    dispatch(resetTimeSelection(currentIndex));

    if (selectedProduct?.available_times?.length === 1) {
      // select entry if there is exactly one.
      onSelectedTimeChange(selectedProduct.available_times[0]);
    }

    dispatch(setTotalCost(currentIndex));
  };

  const onNextBtnClick = () => {
    currentIndex < numOfMembers - 1 &&
      dispatch(setCurrentIndex(currentIndex + 1));
  };
  const onPrevBtnClick = () => {
    currentIndex > 0 && dispatch(setCurrentIndex(currentIndex - 1));
  };

  const {
    data: SKILL_LEVELS,
    loading: SL_LOADING,
    error: SL_ERROR,
  } = useQuery(GET_SKILL_LEVELS, {
    variables: {
      skillLevelInput: {
        dateOfBirth: dateToBackendFormat(member.birth_date),
        checkinDate: dateToBackendFormat(checkInDate),
        sportType: member.sport_type?.id,
      },
    },
    skip: !member.sport_type,
  });

  const {
    data: SPORT_TYPES,
    loading: ST_LOADING,
    error: ST_ERROR,
  } = useQuery(GET_SPORT_TYPES);

  const [addToCart] = useMutation(ADD_TO_CART);

  useEffect(() => {
    setSkillLevels(SKILL_LEVELS?.skillLevels);
  }, [SKILL_LEVELS, member.skill_level]);

  useEffect(() => {
    setSportTypes(SPORT_TYPES?.sportTypes);
  }, [SPORT_TYPES, member.sport_type]);

  const onSportTypeSelect = (sportType: SportType) => {
    dispatch(
      editMember({
        index: currentIndex,
        key: MEMBER_KEYS.SportType,
        value: sportType,
      })
    );
    dispatch(
      editMember({
        index: currentIndex,
        key: MEMBER_KEYS.Skill_Level,
        value: undefined,
      })
    );
    dispatch(resetSelections(currentIndex));
  };

  const onSkillLevelSelect = (skillLevel: SkillLevel) => {
    dispatch(
      editMember({
        index: currentIndex,
        key: MEMBER_KEYS.Skill_Level,
        value: skillLevel,
      })
    );
    dispatch(resetSelections(currentIndex));
  };

  const onSelectedTimeChange = (time: StartTime) => {
    dispatch(
      editMember({
        index: currentIndex,
        key: MEMBER_KEYS.Time,
        value: time,
      })
    );
    dispatch(setTotalCost(currentIndex));
  };

  const onCrossSellingProductsSelect = (
    e: React.ChangeEvent<HTMLInputElement>,
    crossSellingProduct: CrossSellingProduct
  ) => {
    let checked = member.cross_selling_products
      ? [...member.cross_selling_products]
      : [];
    const isAlreadySaved =
      checked.filter(({ id }) => id === crossSellingProduct?.id).length > 0;
    if (e.target.checked && !isAlreadySaved) {
      checked?.push(crossSellingProduct);
      dispatch(
        editMember({
          index: currentIndex,
          key: MEMBER_KEYS.Cross_Selling_Products,
          value: checked,
        })
      );
    } else if (!e.target.checked && isAlreadySaved) {
      checked = checked?.filter(({ id }) => id !== crossSellingProduct.id);
      dispatch(
        editMember({
          index: currentIndex,
          key: MEMBER_KEYS.Cross_Selling_Products,
          value: checked,
        })
      );
    }
    if (member.time?.timeOneFrom) {
      dispatch(setTotalCost(currentIndex));
    }
  };

  const onShoppingCartBtnClick = async () => {
    try {
      const items = getCartItems(members);

      setIsPending(true);

      const result = await addToCart({ variables: { items } });

      if (result.data?.addToCart?.token) {
        await goToShoppingCartPage(result.data.addToCart.token);
      } else {
        console.error("No token received for cart items");
      }
    } catch (error) {
      console.error("Error adding items to cart:", error);
    } finally {
      setIsPending(false);
    }
  };

  const isDateRangeValid =
    member?.date_range?.begin_date && member?.date_range?.end_date;

  return (
    <>
      <div className="flex flex-wrap lg:flex-nowrap justify-center overflow-y-auto lg:overflow-y-visible h-[calc(100vh-85px)] md:h-fit p-4 md:pt-0">
        <div className="w-full md:w-auto md:grow md:min-w-[346.5px] max-w-[600px] flex flex-col gap-2 lg:pr-12">
          <h2 className="text-general text-base md:text-xl font-semibold">
            {member.first_name + " " + member.last_name + " "}
            <span>({member.age})</span>
          </h2>
          <div className=" flex flex-col xl:flex-row w-full gap-2 xl:gap-4 ">
            <div className="w-full xl:w-3/5">
              <DropDown<SportType>
                selectedItem={member.sport_type}
                label={translate("sport")}
                items={sportTypes}
                onItemSelect={onSportTypeSelect}
              />
            </div>
            <div className="w-full xl:w-2/5">
              <DropDown<SkillLevel>
                selectedItem={member.skill_level}
                label={translate("skill_levels")}
                items={skillLevels}
                onItemSelect={onSkillLevelSelect}
                infoButtonLink={"https://www.skischule-serfaus.com/"}
              />
            </div>
          </div>
          <>
            {ST_LOADING || SL_LOADING ? (
              <Spinner />
            ) : !member.sport_type || !member.skill_level ? (
              <div className="w-full h-36 flex-v-center align-middle text-center text-gray-400 text-grow">
                <p>
                  {translate("please_choose_both_sport_and_skill_level_first")}
                </p>
              </div>
            ) : ST_ERROR || SL_ERROR || !skillLevels?.length ? (
              <div className="w-full h-36 flex-v-center align-middle text-center text-gray-400 text-grow">
                <p>
                  {translate(
                    "there_are_no_available_options_that_match_your_selection"
                  )}
                </p>
              </div>
            ) : (
              <>
                <div>
                  <p className=" text-gray-500 text-xs lg:text-sm font-semibold mb-1">
                    {translate("select_the_date_you_want")}
                  </p>

                  <button
                    className={`bg-light-gray text-white font-semibold h-8 md:h-10 flex gap-3 items-center justify-center px-1 text-xs lg:text-sm relative w-full ${
                      isDateRangeValid ? "bg-bright-green" : ""
                    }`}
                    onClick={() => {
                      setIsDateRangeModalOpen(true);
                    }}
                  >
                    <>
                      <p className="text-xl md:text-2xl">
                        <AiOutlineCalendar />
                      </p>
                      {(member?.date_range?.begin_date
                        ? member?.date_range.begin_date
                        : checkInDate
                        ? checkInDate
                        : "") +
                        " " +
                        translate("until") +
                        " "}
                      {member?.date_range?.end_date
                        ? member?.date_range.end_date
                        : "-"}
                    </>
                    {isDateRangeValid && (
                      <span className="absolute right-2">
                        <FaCheck size={16} className="text-white" />
                      </span>
                    )}
                  </button>
                  {isDateRangeModalOpen && (
                    <>
                      <input
                        type="checkbox"
                        id="date-range-modal"
                        className="modal-toggle bg-transparent"
                        readOnly
                        checked
                      />
                      <DateRangeModal
                        onClose={() => {
                          setIsDateRangeModalOpen(false);
                        }}
                      />
                    </>
                  )}
                </div>
                {products.length > 0 && (
                  <div className="hover:cursor-pointer">
                    <ProductSlotOptions
                      selectedProduct={selectedProduct}
                      onSelect={setSelectedProduct}
                      products={products}
                    />
                  </div>
                )}
                {selectedProduct && (
                  <div>
                    <p className=" text-gray-500 text-xs lg:text-sm font-semibold mb-1 mt-2">
                      {"" + translate("choose_a_start_time")}
                    </p>

                    <TimeSlotOptions
                      onSelectedTimeChange={onSelectedTimeChange}
                      product={selectedProduct}
                      days={days ? days : 0}
                    />
                  </div>
                )}
                <CrossSellingProducts
                  products={
                    selectedProduct?.available_cross_selling_products || []
                  }
                  onProductSelectionChange={onCrossSellingProductsSelect}
                />
                <div className="flex justify-center mb-3 lg:mb-0">
                  <button
                    className={`p-1 md:p-2 text-white bg-bloody-red ${
                      currentIndex === 0 ? "hidden" : "block"
                    }`}
                    onClick={onPrevBtnClick}
                  >
                    <BsChevronLeft size={23} />
                  </button>
                  <button
                    className="flex items-center p-1 md:p-2 ml-4 text-white bg-bloody-red text-xs md:text-base disabled:bg-red-300"
                    onClick={
                      showShoppingCartBtn
                        ? onShoppingCartBtnClick
                        : onNextBtnClick
                    }
                    disabled={
                      showShoppingCartBtn
                        ? !members.every(isMemberCompleted)
                        : !isMemberCompleted(member)
                    }
                  >
                    {showShoppingCartBtn ? (
                      <>
                        <div className="flex items-center justify-center w-[23px] h-[23px]">
                          {isPending ? (
                            <div role="status">
                              <svg
                                aria-hidden="true"
                                className="inline w-[20px] h-[20px] text-gray-200 animate-spin dark:text-white fill-bloody-red"
                                viewBox="0 0 100 101"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                  fill="currentColor"
                                />
                                <path
                                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                  fill="currentFill"
                                />
                              </svg>
                              <span className="sr-only">Loading...</span>
                            </div>
                          ) : (
                            <TiShoppingCart size={23} />
                          )}
                        </div>
                        <p className="px-2">
                          {translate("in_the_shopping_cart")}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="px-2">
                          {translate("next") + " " + translate("person")}
                        </p>
                        <BsChevronRight size={23} />
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </>
        </div>
        <div className=" flex flex-col justify-start items-center w-full md:w-72 self-baseline">
          <TotalCost />
          {member.skill_level?.icon?.url && (
            <img
              src={member.skill_level.icon.url}
              alt={member.sport_type?.name ?? "snow sport"}
              className="h-48 w-48 md:h-64 md:w-64 pt-5  mt-[4%] 2xl:mt-[20%]"
            />
          )}
        </div>
      </div>
    </>
  );
};

export default MembersFormComponent;
